import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import {
  useTagsList,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
  type Tag,
  type CreateTagPayload,
  type UpdateTagPayload,
} from './hooks/useTags';

/**
 * Predefined color palette for quick selection.
 */
const COLOR_PALETTE = [
  '#EF4444', // red
  '#F97316', // orange
  '#EAB308', // yellow
  '#22C55E', // green
  '#06B6D4', // cyan
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#6B7280', // gray
  '#1F2937', // dark
];

/**
 * TagsPage — CRUD management page for user-defined tags.
 * Tags can be associated with contacts and support name + optional hex color.
 */
export function TagsPage() {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: tags, isLoading } = useTagsList();
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();
  const deleteMutation = useDeleteTag();

  const sortedTags = useMemo(
    () => [...(tags ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [tags],
  );

  const handleCreate = () => {
    setEditingTag(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setIsEditorOpen(true);
  };

  const handleSave = (data: CreateTagPayload | UpdateTagPayload) => {
    if (editingTag) {
      updateMutation.mutate(
        { id: editingTag.id, data: data as UpdateTagPayload },
        {
          onSuccess: () => {
            setIsEditorOpen(false);
            setEditingTag(null);
          },
        },
      );
    } else {
      createMutation.mutate(data as CreateTagPayload, {
        onSuccess: () => {
          setIsEditorOpen(false);
          setEditingTag(null);
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => setDeleteConfirmId(null),
    });
  };

  const handleToggleActive = (tag: Tag) => {
    updateMutation.mutate({ id: tag.id, data: { is_active: !tag.isActive } });
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"
            role="status"
            aria-label="Loading tags"
          />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Tags"
          description="Manage tags that can be associated with contacts. Tags help categorize and filter contacts."
          action={
            <Button size="sm" onClick={handleCreate}>
              Add Tag
            </Button>
          }
        />
        <CardContent>
          {sortedTags.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No tags configured yet. Add your first tag to get started.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead>
                  <tr className="border-b border-border text-left dark:border-border-dark">
                    <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                      Color
                    </th>
                    <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">Code</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">Name</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                      Description
                    </th>
                    <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="pb-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border dark:divide-border-dark">
                  {sortedTags.map((tag) => (
                    <tr key={tag.id}>
                      <td className="py-3 pr-4">
                        {tag.color ? (
                          <span
                            className="inline-block h-5 w-5 rounded-full border border-gray-200 dark:border-gray-600"
                            style={{ backgroundColor: tag.color }}
                            aria-label={`Color: ${tag.color}`}
                          />
                        ) : (
                          <span
                            className="inline-block h-5 w-5 rounded-full border border-dashed border-gray-300 dark:border-gray-600"
                            aria-label="No color"
                          />
                        )}
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs text-gray-600 dark:text-gray-400">
                        {tag.code}
                      </td>
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">
                        {tag.name}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                        {tag.description || '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={tag.isActive ? 'success' : 'default'} size="sm" dot>
                          {tag.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(tag)}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(tag)}>
                            {tag.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirmId(tag.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {sortedTags.length > 0 && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {sortedTags.filter((t) => t.isActive).length} active / {sortedTags.length} total tag
              {sortedTags.length !== 1 ? 's' : ''}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {isEditorOpen && (
        <TagEditorModal
          tag={editingTag}
          onSave={handleSave}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingTag(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <Modal open onClose={() => setDeleteConfirmId(null)} title="Delete Tag" size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to delete this tag? It will be removed from all associated
              contacts. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteMutation.isPending}
                onClick={() => handleDelete(deleteConfirmId)}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Tag Editor Modal ─────────────────────────────────────────────────────────

interface TagEditorModalProps {
  tag: Tag | null;
  onSave: (data: CreateTagPayload | UpdateTagPayload) => void;
  onClose: () => void;
  isSaving: boolean;
}

function TagEditorModal({ tag, onSave, onClose, isSaving }: TagEditorModalProps) {
  const [form, setForm] = useState({
    name: tag?.name ?? '',
    code: tag?.code ?? '',
    description: tag?.description ?? '',
    color: tag?.color ?? '',
  });

  /** Auto-generate code from name (only when creating and code hasn't been manually edited) */
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(!!tag);

  const generateCode = (name: string): string => {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
  };

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      ...(!codeManuallyEdited ? { code: generateCode(value) } : {}),
    }));
  };

  const handleCodeChange = (value: string) => {
    setCodeManuallyEdited(true);
    setForm((prev) => ({ ...prev, code: value.toLowerCase().replace(/[^a-z0-9-]/g, '') }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: form.name,
      code: form.code,
      description: form.description || null,
      color: form.color || null,
    });
  };

  return (
    <Modal open onClose={onClose} title={tag ? 'Edit Tag' : 'New Tag'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => handleNameChange(e.target.value)}
          required
          maxLength={100}
          placeholder="e.g., VIP, High Priority, Tax Return"
        />
        <Input
          label="Code"
          value={form.code}
          onChange={(e) => handleCodeChange(e.target.value)}
          required
          maxLength={50}
          placeholder="e.g., vip, high-priority, tax-return"
          className="font-mono"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          maxLength={250}
          placeholder="Optional description of this tag"
        />

        {/* Color Selection */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Color (optional)
          </label>
          <div className="space-y-3">
            {/* Palette swatches */}
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.color === color
                      ? 'border-gray-900 ring-2 ring-brand-500 ring-offset-1 dark:border-white'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setForm((prev) => ({ ...prev, color }))}
                  aria-label={`Select color ${color}`}
                  title={color}
                />
              ))}
              {/* Clear color button */}
              <button
                type="button"
                className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-transform hover:scale-110 ${
                  !form.color
                    ? 'border-gray-900 ring-2 ring-brand-500 ring-offset-1 dark:border-white'
                    : 'border-dashed border-gray-300 dark:border-gray-600'
                }`}
                onClick={() => setForm((prev) => ({ ...prev, color: '' }))}
                aria-label="Clear color"
                title="No color"
              >
                <svg
                  className="h-3 w-3 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Color picker + hex input (same pattern as BrandingPage) */}
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color || '#3B82F6'}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, color: e.target.value.toUpperCase() }))
                }
                className="h-10 w-10 cursor-pointer rounded-lg border border-border dark:border-border-dark"
                aria-label="Tag color picker"
              />
              <Input
                value={form.color}
                onChange={(e) => setForm((prev) => ({ ...prev, color: e.target.value }))}
                placeholder="#3B82F6"
                maxLength={7}
                className="flex-1 font-mono"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        {form.name && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Preview
            </label>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
              style={{
                backgroundColor: form.color ? `${form.color}20` : '#E5E7EB',
                color: form.color || '#374151',
                border: `1px solid ${form.color || '#D1D5DB'}`,
              }}
            >
              {form.color && (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: form.color }}
                />
              )}
              {form.name}
            </span>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSaving}
            disabled={!form.name.trim() || !form.code.trim()}
          >
            {tag ? 'Update Tag' : 'Create Tag'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
